import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { createFileLogger } from '../../../../utils/logger';
import { detectAIContent } from '../../services/ai/aiDetectionService';
import { createDocumentReader } from "../../services/doc/DocumentReader";
import { docsAddCommentCommand } from './docs_add_comment';

const log = createFileLogger('AI_DETECT_TOOL');

export const aiDetectCommand: SimpleDocCommand = {
  id: 'ai_detect',
  description: `Detect AI-generated content in a document iteratively and automatically add inline comments

**Purpose**: Analyze document text for AI-generated characteristics using hybrid detection (LLM + stylometric analysis)

**Parameters**:
• id: Document ID to analyze (e.g. "doc_large_pig_coconut")

**Returns**: 
- AI likelihood score (0-1, averaged across batches)
- Classification label (likely_human, uncertain, likely_ai, very_likely_ai)
- Number of comments automatically added throughout the document
- Processing stats (batches analyzed)

**How it works**:
1. Processes document in batches of 5 nodes at a time
2. Analyzes each batch for AI-like patterns
3. AUTOMATICALLY adds inline comments to suspicious sections in real-time
4. Shows progress: "Analyzing section 1/10..."
5. Adds a summary comment at document start
6. Returns aggregated results

**Benefits**:
- ✅ Works with documents of ANY length (no truncation)
- ✅ Precise node-level targeting (no position mapping issues)
- ✅ Progressive feedback as it works through document
- ✅ Partial results if interrupted

**Usage**:
When user asks to "detect AI content", "check for AI writing", "analyze if this is AI-generated":
- Just call ai_detect with the document ID
- Comments are added automatically as batches complete!

**Important**: This is a probabilistic assessment, not definitive proof. Results should be interpreted as indicators.`,
  
  logicalName: 'ai_detect',
  version: 'v1',
  appType: 'docs',
  
  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.data) {
        const data = result.data;
        return {
          type: 'ai_detection_result',
          summary: result.message || `AI Detection: ${data.label}`,
          data: {
            runId: data.run_id,
            aiLikelihood: data.ai_likelihood,
            confidence: data.confidence,
            label: data.label,
            explanations: data.explanations || [],
            suspiciousSpans: data.suspicious_spans || [],
            features: data.features || {},
            documentId: toolInput?.id || 'unknown',
            comments_added: data.comments_added || 0,
            comments_failed: data.comments_failed || 0
          },
          meta: {
            duration: result.metadata?.executionTime
          }
        };
      }
      
      return {
        type: 'tool_summary',
        summary: 'AI detection complete',
        data: result?.data || {}
      };
      
    } catch (error) {
      console.warn('[AI_DETECT] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'AI detection complete',
        data: {}
      };
    }
  },
  
  formatResultPreview: (result: any, toolInput: any) => {
    try {
      if (result?.data) {
        const data = result.data;
        const likelihood = Math.round((data.ai_likelihood || 0) * 100);
        return `${data.label || 'Unknown'} (${likelihood}% AI likelihood)`;
      }
      return 'AI detection complete';
    } catch (error) {
      console.warn('[AI_DETECT_FORMATTER] Error:', error);
      return 'AI detection complete';
    }
  },
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document ID to analyze for AI-generated content (e.g. "doc_large_pig_coconut")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          return null;
        }
      }
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = performance.now();
    
    try {
      log.log('[AI_DETECT] Starting AI detection', { params });
      
      const { id: rawDocId } = params;
      
      if (!rawDocId) {
        return createErrorResult(
          'Document ID is required',
          'id parameter is required',
          'validation'
        );
      }
      
      // Use DocumentReader to get text content
      const { reader, error: readerError } = await createDocumentReader(rawDocId, context);
      if (readerError || !reader) {
        log.error('[AI_DETECT] Failed to create document reader:', {
          id: rawDocId,
          error: readerError || 'Reader creation failed'
        });
        return createErrorResult(
          'Document not accessible',
          readerError || `Could not access document (llmId: ${rawDocId})`,
          'system'
        );
      }
      
      // Read entire document
      const allContent = reader.readAll();
      const allNodes = allContent.nodes || [];
      
      if (allNodes.length === 0) {
        return createErrorResult(
          'Document is empty',
          'No content to analyze',
          'validation'
        );
      }
      
      log.log('[AI_DETECT] Document loaded', {
        docId: rawDocId,
        nodeCount: allNodes.length,
        totalWords: allNodes.reduce((sum: number, n: any) => sum + (n.text?.split(/\s+/).length || 0), 0)
      });
      
      // Process nodes in batches for iterative detection
      const BATCH_SIZE = 5; // Analyze 5 nodes at a time
      let totalCommentsAdded = 0;
      let totalCommentsFailed = 0;
      const allExplanations: string[] = [];
      let overallScore = 0;
      let overallLabel = 'Unknown';
      let overallConfidence = 'low';
      let batchCount = 0;
      
      for (let i = 0; i < allNodes.length; i += BATCH_SIZE) {
        // Check for cancellation
        if (context.signal?.aborted) {
          log.log('[AI_DETECT] Detection cancelled by user');
          return createErrorResult(
            'AI detection cancelled',
            `Cancelled after processing ${batchCount} batches. Added ${totalCommentsAdded} comment(s) before cancellation.`,
            'cancelled'
          );
        }
        const batch = allNodes.slice(i, Math.min(i + BATCH_SIZE, allNodes.length));
        const batchText = batch.map((node: any) => node.text || '').join('\n\n').trim();
        
        // Skip empty batches
        if (!batchText || batchText.length < 50) {
          log.log(`[AI_DETECT] Skipping empty batch ${i / BATCH_SIZE + 1}`);
          continue;
        }
        
        batchCount++;
        const batchNum = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(allNodes.length / BATCH_SIZE);
        
        // Progress update
        if (context.onProgress) {
          context.onProgress({
            type: 'streaming_update',
            toolUseId: context.executionId,
            message: `Analyzing section ${batchNum}/${totalBatches}...`
          });
        }
        
        log.log(`[AI_DETECT] Processing batch ${batchNum}/${totalBatches}`, {
          nodeRange: `${i + 1}-${i + batch.length}`,
          textLength: batchText.length
        });
        
        // Analyze this batch
        const batchResult = await detectAIContent(`${rawDocId}_batch_${batchNum}`, batchText);
        
        // Check for cancellation after potentially long operation
        if (context.signal?.aborted) {
          log.log('[AI_DETECT] Detection cancelled during analysis');
          return createErrorResult(
            'AI detection cancelled',
            `Cancelled after analyzing ${batchCount} batches. Added ${totalCommentsAdded} comment(s) before cancellation.`,
            'cancelled'
          );
        }
        
        // Accumulate overall metrics (weighted average)
        overallScore = (overallScore * (batchCount - 1) + batchResult.ai_likelihood) / batchCount;
        overallLabel = batchResult.label; // Use latest
        overallConfidence = batchResult.confidence;
        allExplanations.push(...batchResult.explanations.slice(0, 2)); // Take top 2 per batch
        
        // Add comments for suspicious spans in THIS batch
        if (batchResult.suspicious_spans && batchResult.suspicious_spans.length > 0) {
          const commentsToAdd: any[] = [];
          
          for (const span of batchResult.suspicious_spans) {
            // Extract excerpt from batchText using span positions
            const excerpt = batchText.substring(span.start, span.end).trim();
            
            if (!excerpt || excerpt.length < 10) continue;
            
            // Find which node in THIS BATCH contains this excerpt
            const normalizedExcerpt = excerpt.replace(/^\n+/, '').trim();
            const searchText = normalizedExcerpt.toLowerCase().substring(0, 30);
            
            for (const node of batch) {
              const nodeText = node.text || '';
              if (nodeText.toLowerCase().includes(searchText)) {
                const targetText = normalizedExcerpt.substring(0, 100);
                
                commentsToAdd.push({
                  nodeId: node.id,
                  targetText: targetText.trim(),
                  commentBody: `**AI Detection Alert**\n\n` +
                    `**Issue:** ${span.reason}`
                });
                
                log.log(`[AI_DETECT] Matched span to node in batch ${batchNum}`, {
                  nodeId: node.id,
                  excerptPreview: excerpt.substring(0, 50)
                });
                break;
              }
            }
          }
          
          // Add comments for this batch
          if (commentsToAdd.length > 0) {
            try {
              const commentResult = await docsAddCommentCommand.handler(
                { id: rawDocId, comments: commentsToAdd },
                context
              );
              
              totalCommentsAdded += commentResult.data?.successCount || 0;
              totalCommentsFailed += commentResult.data?.failCount || 0;
              
              log.log(`[AI_DETECT] Batch ${batchNum} comments added`, {
                added: commentResult.data?.successCount || 0,
                failed: commentResult.data?.failCount || 0
              });
            } catch (error: any) {
              log.error(`[AI_DETECT] Failed to add comments for batch ${batchNum}:`, error);
              totalCommentsFailed += commentsToAdd.length;
            }
          }
        }
      }
      
      // Add summary comment at the very beginning
      if (allNodes.length > 0 && totalCommentsAdded > 0) {
        const firstNode = allNodes[0];
        const firstNodeText = firstNode.text || '';
        
        if (firstNodeText.trim().length >= 4) {
          const summaryTargetText = firstNodeText.trim().substring(0, 50);
          const summaryBody = `**AI Detection Summary**\n\n` +
            `**Overall Assessment:** ${overallLabel}\n\n` +
            `**AI Likelihood Score:** ${Math.round(overallScore * 100)}%\n\n` +
            `**Confidence:** ${overallConfidence}\n\n` +
            `**Comments Added:** ${totalCommentsAdded}\n\n` +
            `**Key Findings:**\n` +
            allExplanations.slice(0, 5).map((exp: string) => `- ${exp}`).join('\n');
          
          try {
            await docsAddCommentCommand.handler(
              {
                id: rawDocId,
                comments: [{
                  nodeId: firstNode.id,
                  targetText: summaryTargetText,
                  commentBody: summaryBody
                }]
              },
              context
            );
            totalCommentsAdded++;
          } catch (error: any) {
            log.error('[AI_DETECT] Failed to add summary comment:', error);
          }
        }
      }
      
      const endTime = performance.now();
      const executionTime = Math.round(endTime - startTime);
      
      log.log('[AI_DETECT] Iterative detection complete', {
        docId: rawDocId,
        batchesProcessed: batchCount,
        overallScore: overallScore,
        overallLabel: overallLabel,
        totalComments: totalCommentsAdded,
        executionTime: Math.round(performance.now() - startTime)
      });
      
      // Return StandardToolResult with aggregated metrics
      return createSuccessResult(
        `AI Detection complete: ${overallLabel} (${Math.round(overallScore * 100)}% AI likelihood) • Added ${totalCommentsAdded} comment(s)`,
        {
          run_id: `run_${Date.now()}_${rawDocId}`,
          ai_likelihood: overallScore,
          confidence: overallConfidence,
          label: overallLabel,
          explanations: [...new Set(allExplanations)].slice(0, 5), // Deduplicate and limit
          suspicious_spans: [], // Not meaningful in iterative mode
          features: {},
          comments_added: totalCommentsAdded,
          comments_failed: totalCommentsFailed,
          batches_processed: batchCount
        },
        {
          metadata: {
            executionTime: Math.round(performance.now() - startTime),
            affectedEntities: [rawDocId]
          }
        }
      );
      
    } catch (error: any) {
      log.error('[AI_DETECT] Detection failed:', error);
      return createErrorResult(
        'AI detection failed',
        error.message || 'Unknown error occurred during AI detection',
        'execution'
      );
    }
  }
};

